/**
 * Unified Building Renderer
 *
 * Shared function for template-aware building rendering.
 * Used by both MapboxCustomLayerService and BuildingEditorMap.
 */

import * as THREE from 'three'
import type { Building3DConfigExtended } from '../BuildingLoaderService'
import type { GeneratedBuilding, GeneratedUnit } from '@/lib/templates/types'
import { SceneBuilder } from '../SceneBuilder'
import { renderSingleRowBuilding, type SingleRowRenderOptions } from './orchestrators/SingleRowOrchestrator'
import { renderSingleUnitBuilding, type SingleUnitRenderOptions } from './orchestrators/SingleUnitOrchestrator'
import { renderDoubleRowBuilding, type DoubleRowRenderOptions } from './orchestrators/DoubleRowOrchestrator'
import { renderOutdoorRectangularBuilding, type OutdoorRenderOptions } from './orchestrators/OutdoorRectangularOrchestrator'
import { renderTRowBuilding, type TRowRenderOptions } from './orchestrators/TRowOrchestrator'
import { renderNestedTRowBuilding, type NestedTRowRenderOptions } from './orchestrators/NestedTRowOrchestrator'
import { renderArchHangarBuilding, type ArchHangarRenderOptions } from './orchestrators/ArchHangarOrchestrator'
import { mergeBuildingGeometries } from './utils/mergeBuilding'

// ============================================================================
// TYPES
// ============================================================================

export interface RenderBuildingOptions {
  /** Enable shadow casting/receiving */
  enableShadows?: boolean
}

/** Orchestrator render function signature */
type OrchestratorRenderer = (
  building: GeneratedBuilding,
  units: GeneratedUnit[],
  options: SingleRowRenderOptions | SingleUnitRenderOptions | DoubleRowRenderOptions | OutdoorRenderOptions | TRowRenderOptions | NestedTRowRenderOptions | ArchHangarRenderOptions
) => THREE.Group

/** Template type for orchestrator registry */
type TemplateType = 'single_row' | 'single_unit_community' | 'double_row' | 'outdoor_rectangular' | 'standard_t_hangar' | 'nested_t_hangar' | 'arch_hangar'

// ============================================================================
// ORCHESTRATOR REGISTRY
// ============================================================================

/**
 * Registry of template type → orchestrator renderer.
 * To add a new template, just add an entry here.
 */
const orchestratorRegistry: Record<TemplateType, OrchestratorRenderer> = {
  single_row: renderSingleRowBuilding,
  single_unit_community: renderSingleUnitBuilding,
  double_row: renderDoubleRowBuilding,
  outdoor_rectangular: renderOutdoorRectangularBuilding,
  standard_t_hangar: renderTRowBuilding,
  nested_t_hangar: renderNestedTRowBuilding,
  arch_hangar: renderArchHangarBuilding,
}

// ============================================================================
// MAIN RENDER FUNCTION
// ============================================================================

/**
 * Render a building using the appropriate template-aware orchestrator.
 *
 * @param building - Building config with optional template data (generatedBuilding, generatedUnits)
 * @param options - Render options (shadows, etc.)
 * @returns THREE.Group representing the rendered building
 */
export function renderBuildingFromConfig(
  building: Building3DConfigExtended,
  options: RenderBuildingOptions = {}
): THREE.Group {
  const { enableShadows = true } = options
  const { templateType, generatedBuilding, generatedUnits } = building

  // Template-aware rendering if we have generated data.
  // Outdoor areas (fuel_depot/exit_point) legitimately have zero units.
  if (
    generatedBuilding &&
    generatedUnits &&
    (generatedUnits.length > 0 || templateType === 'outdoor_rectangular')
  ) {
    const renderOptions = {
      buildingId: building.id,
      position: building.position,
      rotationDeg: building.rotation,
      wallColor: building.wallColor,
      floorColor: building.floorColor,
      wallThickness: (building.typeConfig?.wall_thickness_ft as number) ?? 0.5,
      enableShadows,
    }

    // Look up orchestrator in registry
    const orchestrator = templateType ? orchestratorRegistry[templateType as TemplateType] : undefined

    if (orchestrator) {
      const raw = orchestrator(generatedBuilding, generatedUnits, renderOptions)
      // Merge same-material geometries → ~90 meshes → ~4 merged meshes per building
      return mergeBuildingGeometries(raw, enableShadows)
    }

    // Unknown template with generated data - log warning
    console.warn(
      `renderBuildingFromConfig: Unknown template "${templateType}" for building "${building.name}", using fallback`
    )
  }

  // Fallback for unknown templates or missing template data
  return SceneBuilder.createBuilding(building, enableShadows)
}
